import React from 'react';

/**
 * Custom illustrated thumbnail for the AI Chat System (live GPT-powered chat).
 * Same visual language as NOTEorious: white canvas, #121110 ink only. Depicts a
 * chat window — AI avatar, incoming/outgoing bubbles, an animated "typing"
 * indicator (streaming responses), and an input bar. SMIL animations run
 * because the card lives inside `.op-page`.
 */
const INK = '#121110';

const AiChatThumb: React.FC = () => (
  <svg
    viewBox="0 0 400 300"
    className="op-thumb w-full h-full bg-white transition-transform duration-700 ease-out group-hover:scale-105"
    preserveAspectRatio="xMidYMid meet"
    role="img"
    aria-label="AI Chat System — live conversational AI"
  >
    {/* Canvas */}
    <rect width="400" height="300" fill="#ffffff" />

    {/* Faint dotted texture */}
    <g fill={INK} opacity="0.05">
      {Array.from({ length: 6 }).map((_, r) =>
        Array.from({ length: 9 }).map((_, c) => (
          <circle key={`${r}-${c}`} cx={22 + c * 45} cy={26 + r * 48} r="1.4" />
        ))
      )}
    </g>

    {/* Chat window frame */}
    <rect x="46" y="40" width="308" height="220" rx="16" fill="#ffffff" stroke={INK} strokeWidth="1.8" />

    {/* Header */}
    <g>
      {/* AI avatar */}
      <circle cx="74" cy="68" r="13" fill="#ffffff" stroke={INK} strokeWidth="1.8" />
      <path d="M74 59 C75.3 65 76 65.7 82 68 C76 70.3 75.3 71 74 77 C72.7 71 72 70.3 66 68 C72 65.7 72.7 65 74 59 Z" fill={INK} />
      {/* Name + subtitle */}
      <rect x="94" y="61" width="66" height="8" rx="4" fill={INK} />
      <rect x="94" y="74" width="40" height="6" rx="3" fill={INK} opacity="0.3" />
      {/* Online status dot */}
      <circle cx="332" cy="68" r="4" fill={INK}>
        <animate attributeName="opacity" values="1;0.3;1" dur="1.8s" repeatCount="indefinite" />
      </circle>
      {/* Divider */}
      <line x1="62" y1="92" x2="338" y2="92" stroke={INK} strokeWidth="1" opacity="0.1" />
    </g>

    {/* Incoming AI bubble */}
    <g>
      <rect x="66" y="104" width="158" height="42" rx="13" fill="#ffffff" stroke={INK} strokeWidth="1.5" />
      <rect x="80" y="118" width="122" height="6" rx="3" fill={INK} opacity="0.16" />
      <rect x="80" y="130" width="94" height="6" rx="3" fill={INK} opacity="0.16" />
    </g>

    {/* Outgoing user bubble (filled) */}
    <g>
      <rect x="194" y="154" width="142" height="38" rx="13" fill={INK} />
      <rect x="208" y="167" width="104" height="6" rx="3" fill="#ffffff" opacity="0.9" />
      <rect x="208" y="179" width="78" height="6" rx="3" fill="#ffffff" opacity="0.5" />
    </g>

    {/* AI typing indicator */}
    <g>
      <rect x="66" y="200" width="60" height="26" rx="13" fill="#ffffff" stroke={INK} strokeWidth="1.5" />
      {[84, 96, 108].map((cx, i) => (
        <circle key={cx} cx={cx} cy="213" r="3.5" fill={INK}>
          <animate
            attributeName="opacity"
            values="0.25;1;0.25"
            dur="1.2s"
            begin={`${i * 0.2}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}
    </g>

    {/* Input bar */}
    <g>
      <rect x="66" y="234" width="224" height="24" rx="12" fill="#ffffff" stroke={INK} strokeWidth="1.4" />
      <rect x="80" y="243" width="96" height="6" rx="3" fill={INK} opacity="0.18" />
      <circle cx="312" cy="246" r="14" fill={INK} />
      <path d="M306 246 h11 M312 240 l6 6 l-6 6" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

export default AiChatThumb;
